<template>
  <div v-if="loading" class="loading-container">
    <v-progress-circular color="#ffd139" indeterminate :size="64" :width="6"></v-progress-circular>
    <div>{{ $t('i18n.preparingContent') }}</div>
  </div>
  <div v-else class="view-container">
    <MainNavBar @navigate="changeActiveTab" />
    <div class="content-container">
      <RouterView />
    </div>
    <FooterBar />
    <MobileMainNavBar />
  </div>

  <div class="alerts-container">
    <FeedbackAlert v-for="alert in alerts" :key="alert.id" :text="alert.text" :feedbackType="alert.feedbackType" @close-alert="removeAlert(alert.id)" />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import router from './router'
import { useAppStore } from './stores/appStore'
import { api, timers } from './utils/HelperFunctions'
import MainNavBar from './components/navbars/MainNavBar.vue'
import MobileMainNavBar from './components/navbars/MobileMainNavBar.vue'
import FeedbackAlert from './components/misc/FeedbackAlert.vue'
import FooterBar from './components/misc/FooterBar.vue'
import { login, refreshTokenPair } from './utils/keyCloak'

export default defineComponent({
  name: 'App',
  components: {
    MainNavBar,
    MobileMainNavBar,
    FeedbackAlert,
    FooterBar
  },
  async beforeMount() {
    const appStore = useAppStore()
    try {
      await router.isReady()

      const authenticated = await login()

      if (authenticated) {
        this.initializeAccessTokenRefresher()
        await this.loadSystemConfiguration()
        timers.start('systemConfiguration', () => this.loadSystemConfiguration(), 60000)
        return
      }

      router.push({ name: 'dashboard' })
    } catch (error) {
      appStore.clearSession()
      console.error('An error occured during app mounting: ', error)
    } finally {
      this.loading = false
    }
  },
  async beforeUnmount() {
    const appStore = useAppStore()
    this.clearTokenRefresherTimer()
    appStore.clearSession()
    timers.stop('systemConfiguration')
  },
  data() {
    return {
      activeTabName: 'dashboard',
      loading: true,
      refreshTokenPollingTimer: undefined as ReturnType<typeof setInterval> | undefined
    }
  },
  methods: {
    changeActiveTab(tabName: string) {
      this.activeTabName = tabName
    },
    initializeAccessTokenRefresher() {
      try {
        console.log('Initializing token pair refresh timer...')
        timers.start('refreshTokenPair', refreshTokenPair, 5 * 1000)
        console.log('Initialized.')
      } catch (error) {
        console.error('Error in access token refresher setup: ', error)
      }
    },
    clearTokenRefresherTimer() {
      timers.stop('refreshTokenPair')
    },
    async loadSystemConfiguration() {
      const appStore = useAppStore()

      try {
        const [
          getDeviceTypesResponse,
          getFormItemTypesResponse,
          getRatingItemTypesResponse
        ] = await Promise.all([
          api('/device-types', 'get'),
          api('/forms-item-types', 'get'),
          api('/rating-item-types', 'get')
        ])

        if (!getDeviceTypesResponse) throw new Error('Failed to fetch device types')
        if (!getFormItemTypesResponse) throw new Error('Failed to fetch form item types')
        if (!getRatingItemTypesResponse) throw new Error('Failed to fetch rating item types')

        appStore.setDeviceTypes(getDeviceTypesResponse.data)
        appStore.setFormItemTypes(getFormItemTypesResponse.data)
        appStore.setRatingTypes(getRatingItemTypesResponse.data)

        console.log('System configuration loaded successfully.')
      } catch (error) {
        console.error('Error loading system configuration: ', error)
        appStore.addAlert('error', 'Couldn\'t load system configuration.')
      }
    },
    removeAlert(alertId: string) {
      const appStore = useAppStore()
      appStore.removeAlert(alertId)
    }
  },
  computed: {
    alerts() {
      const appStore = useAppStore()
      return appStore.alerts
    }
  }
})
</script>

<style lang="scss">
@import "./styles/base.scss";
@import "./styles/fonts.scss";
@import "./styles/colors.scss";

html,
body,
#app {
  height: 100%;
  overflow-y: hidden;
}

.alerts-container {
  position: fixed;
  bottom: 6em;
  right: 2.5em;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 1em;
  max-width: 100%;
}

@media (max-width: 960px) {
  .alerts-container {
    left: 50%;
    right: auto;
    transform: translateX(-50%);
    min-width: 25em;
  }
}

.view-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;

  .content-container {
    background-color: $grey-3;
    flex: 1; // Make this container take up remaining space between the navbar and footer
    height: 100%;
    overflow: auto;
  }
}

.loading-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2em;
  /* background-color: aqua; */
}
</style>
